/**
 * 自動実行設定スキーマ — スケジュール定義群と WebHook エンドポイント設定群の SSOT。
 *
 * ワークスペース設定（`WorkspaceSchema.automationConfig`）の一部として保持され、
 * クラウド同期・プラグイン配布の往復経路に載る「設定（desired state）」のみを表す。
 *
 * 不変条件（このスキーマには現れない）:
 * - 端末固有の実行時状態（`lastExecutedAt` / `continuationTaskId` / once 実行後の `completed` 遷移）
 * - サーバー生成のエンドポイント実体（`endpointId` / `receiveUrl`）
 * - 秘密情報の実値（HMAC シークレット・トークン等。`secretRef` の参照キーのみ保持）
 *
 * スケジュール実行時状態は desktop 側の `schedule-runtime-store` が、
 * WebHook 実体・秘密はクラウドの `WebhookEndpointService` がそれぞれ SSOT として保持する。
 */
import { z } from 'zod';
import { ModelSelectionSchema, ReasoningLevelSchema } from './base-schemas';
import { EndpointAuthSchema, QueueControlSchema } from './webhook-schemas';
// ========================================
// プリセット設定（SSOT。desktop schedule-types は本定義を参照する）
// ========================================
export const DailyConfigSchema = z.object({
    type: z.literal('daily'),
    hour: z.number().int().min(0).max(23),
    minute: z.number().int().min(0).max(59),
});
export const WeeklyConfigSchema = z.object({
    type: z.literal('weekly'),
    dayOfWeek: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    minute: z.number().int().min(0).max(59),
});
export const MonthlyConfigSchema = z.object({
    type: z.literal('monthly'),
    dayOfMonth: z.number().int().min(1).max(31),
    hour: z.number().int().min(0).max(23),
    minute: z.number().int().min(0).max(59),
});
export const EveryNHoursConfigSchema = z.object({
    type: z.literal('every_n_hours'),
    hours: z.number().int().min(1).max(24),
});
export const OnceConfigSchema = z.object({
    type: z.literal('once'),
    dateTime: z.string(),
});
export const PresetConfigSchema = z.discriminatedUnion('type', [
    DailyConfigSchema,
    WeeklyConfigSchema,
    MonthlyConfigSchema,
    EveryNHoursConfigSchema,
    OnceConfigSchema,
]);
// ========================================
// セッションモード（SSOT）
// ========================================
/**
 * セッションモード
 * - isolated: 実行ごとに新規タスク（現状互換）
 * - continued: スケジュール専用の常駐タスクへ会話を追記（文脈保持・カード集約）
 */
export const SessionModeSchema = z.enum(['isolated', 'continued']);
/** セッションモードの既定値（後方互換: 未設定は新規モード） */
export const DEFAULT_SESSION_MODE = 'isolated';
// ========================================
// スケジュール設定サブセット（実行時状態・identity・secret を含まない）
// ========================================
/** スケジュールのプリセット種別（cron を含む） */
export const SchedulePresetTypeSchema = z.enum([
    'daily',
    'weekly',
    'monthly',
    'every_n_hours',
    'once',
    'cron',
]);
/**
 * スケジュール定義の純粋な構成（同期/配布対象）。
 * `enabled` はユーザーの有効/無効意図。once 実行後の `completed` 遷移はローカル実行時状態側で扱う。
 */
export const ScheduleSettingsSchema = z
    .object({
    /** 安定キー（実行時状態ストアとの対応・配布の同一性に用いる） */
    id: z.string().uuid(),
    name: z.string().min(1),
    cronExpression: z.string(),
    presetType: SchedulePresetTypeSchema,
    presetConfig: PresetConfigSchema.nullable(),
    prompt: z.string().min(1),
    selectedModel: ModelSelectionSchema.nullable(),
    /** エフォート（推論レベル）。未設定ならワークスペースの優先エフォート → チャットのエフォートを使う */
    reasoningLevel: ReasoningLevelSchema.optional(),
    sessionMode: SessionModeSchema,
    enabled: z.boolean(),
})
    .superRefine((val, ctx) => {
    // once は once 形式の presetConfig（dateTime）を必須とする。
    // 欠落すると評価器（evaluateOnceSchedule）が無言で発火しないため、配布/手書き設定の段階で弾く。
    if (val.presetType === 'once' && val.presetConfig?.type !== 'once') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['presetConfig'],
            message: 'presetType が "once" のときは once 形式の presetConfig（dateTime）が必要です',
        });
    }
    // once 以外（各プリセット/cron）は cronExpression を必須とする（空だと一切発火しないため）。
    if (val.presetType !== 'once' && val.cronExpression.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cronExpression'],
            message: 'once 以外のスケジュールには cronExpression が必要です',
        });
    }
});
// ========================================
// WebHook エンドポイント設定サブセット（secret/identity を含まない定義）
// ========================================
/**
 * WebHook の持ち出し可能な定義。秘密・受信 URL・エンドポイント ID は持たず、
 * `authType` は秘密を除いた構成のみを表す（既存 `EndpointAuthSchema` を流用）。
 */
export const WebhookEndpointSettingsSchema = z.object({
    /** 安定キー（クラウド実体 `workspaceDefinitionId` への backlink 用） */
    id: z.string().uuid(),
    name: z.string().min(1),
    promptTemplate: z.string(),
    authType: EndpointAuthSchema,
    queueControl: QueueControlSchema,
    selectedModel: ModelSelectionSchema.nullable(),
    /** エフォート（推論レベル）。未設定ならワークスペースの優先エフォート → チャットのエフォートを使う */
    reasoningLevel: ReasoningLevelSchema.optional(),
    enabled: z.boolean(),
    /**
     * 公開/配布時に秘密を要する場合のテンプレート参照キー（例: `webhook.<id>.secret`）。
     * 実値は持たず、インストール時にユーザー入力で解決して実体側へ渡す。
     */
    secretRef: z.string().nullable().default(null),
    /**
     * 登録ハンドシェイクで署名シークレットを確立する場合 true（Asana 等）。
     * このとき作成時に秘密入力を要さず、最初の受信で確立する。
     */
    establishSecretViaHandshake: z.boolean().optional(),
    /**
     * 署名シークレットの発行元。インストール時の挙動を決める。
     * - `auto`（既定・未指定含む）: 受信側で生成する方式（GitHub 等）。install 時はサーバー自動生成し、
     *   受信URLと共に提示する（入力不要）。オーサリングの既定（空欄＝自動生成）に合わせる。
     * - `manual`: 送信サービスが固定発行する方式（Slack/Stripe/LINE 等）。作成者がシークレットを入力した
     *   ことを表し、install 時はその値の入力を要する（自動生成すると署名検証が失敗するため）。
     */
    secretProvisioning: z.enum(['auto', 'manual']).optional(),
});
// ========================================
// 自動実行設定の集約
// ========================================
/**
 * ワークスペースが所有する自動実行設定の集約ルート。
 * `schedules` / `webhookEndpoints` を内包し、未設定の配列は空配列へ補完する。
 */
export const AutomationConfigSchema = z
    .object({
    schedules: z.array(ScheduleSettingsSchema).default([]),
    webhookEndpoints: z.array(WebhookEndpointSettingsSchema).default([]),
})
    .nullable();
//# sourceMappingURL=automation-schemas.js.map